import { format } from "date-fns"
import { loadAllEntities } from "../../../cached-data/actions"
import { SchemaDashboardRowData, tProject } from "../../../cached-data/types"
import { DATE_ONLY_FORMAT } from "../../../common/constants"
import { iCurrentUser, tFeatures, Thunk } from "../../../common/types"
import ManageSchemaAccessButton from "../schemas/ManageSchemaAccessButton"
import {
    integerColDefCreator,
    stringColDefCreator,
    buttonColDefCreator,
    slideToggleColDefCreator,
    workflowButtonColDefCreator,
    emailListColDefCreator,
    schemaEditorButtonColDefCreator,
} from "./standard-col-def-creators"
import { getFlagEnabled } from "../../../getFlagValue"
import { tTableSettings } from "../types"
import { tColDef } from "../../../new-custom-forms/types"

type AgRowNode = {
    data: SchemaDashboardRowData
}

export const getFieldFormsDashboardSettings = (featureFlags: tFeatures, currentProject: tProject, currentUser: iCurrentUser): tTableSettings => {
    const { user_role } = currentUser
    const canManage = user_role === "ADMIN"
    const is_rhumbix_user = currentUser.employee.is_rhumbix_user
    const settings = {
        tableName: "Field Forms",
        navId: "fieldForms",
        resources: ["companyFormSchemasForDashboard"],
        filters: [],
        additionalQueryParams: {
            include_annotations: true,
            "fields!": ["available_statuses", "initial_status", "permissions", "schema", "schema_type", "status_transitions", "template", "ui_schema"],
            ...(getFlagEnabled("WA-8120-ff-projects-company-settings") ? { skip_projects: true } : null),
        },
        initAction: (): Thunk => dispatch => {
            dispatch(loadAllEntities("companyGroups"))
            dispatch(loadAllEntities("projects", { forceBigPageSize: true }))
        },
        colDefs: [
            stringColDefCreator({
                field: "/name",
                headerName: "Name",
                sort: "asc",
                pinned: "left",
            }),
            stringColDefCreator({
                editable: canManage,
                field: "/description",
                headerName: "Description",
                width: 300,
            }),
            {
                ...slideToggleColDefCreator({
                    headerName: getFlagEnabled("WA-8934-ff-force-send-emails") ? "Send To Subscribed" : "Notifications",
                    field: "/send_email",
                    shouldPreventActionFocus: true,
                    editable: true,
                    width: 175,
                }),
                headerTooltip: "Enable this to send email notifications for this form. This setting impacts all users.",
            },
            {
                ...emailListColDefCreator({
                    headerName: "Additional Emails",
                    field: "/force_send_emails",
                    hide: !getFlagEnabled("WA-8934-ff-force-send-emails"),
                    width: 300,
                    editable: canManage ? (getFlagEnabled("WA-9111-add-dl-to-force-email-view") ? ({ data }: AgRowNode) => data.force_send_emails_editable !== false : true) : false,
                    headerTooltip:
                        // eslint-disable-next-line max-len
                        "Will receive emails for updates regardless of Send To Subscribed status or Project Opt-In.",
                }),
                customTooltip: getFlagEnabled("WA-9111-add-dl-to-force-email-view")
                    ? ({ data }: AgRowNode) => ({
                          tooltipShouldRender: data.force_send_emails_editable === false,
                          data: {
                              messages: [
                                  // eslint-disable-next-line max-len
                                  "This form has variants. Contact support@rhumbix.com to configure email permissions.",
                              ],
                          },
                      })
                    : undefined,
            },
            stringColDefCreator({
                headerName: "Groups with Auto-Add",
                // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
                valueGetter: ({ data: { number_groups_with_auto_add, total_groups } }: AgRowNode) => {
                    if (getFlagEnabled("WA-7632-project-specific-modifiers")) {
                        return `${number_groups_with_auto_add} of ${total_groups}`
                    }

                    if (number_groups_with_auto_add === total_groups) {
                        return total_groups === 0 ? "none" : `all ${total_groups}`
                    }

                    return `${number_groups_with_auto_add} of ${total_groups}`
                },
                width: 300,
            }),
            stringColDefCreator({
                headerName: "Projects",
                // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
                valueGetter: ({ data: { number_projects, total_projects } }: AgRowNode) => {
                    if (getFlagEnabled("WA-7632-project-specific-modifiers")) {
                        return `${number_projects} of ${total_projects}`
                    }

                    if (number_projects === total_projects) {
                        return total_projects === 0 ? "none" : `all ${total_projects}`
                    }

                    return `${number_projects} of ${total_projects}`
                },
                width: 100,
            }),
            integerColDefCreator({
                cellStyle: { textAlign: "right" },
                field: "/number_generated",
                headerName: "Forms Generated",
                width: 150,
            }),
            stringColDefCreator({
                headerName: "Last Used",
                field: "/last_used",
                // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
                valueFormatter: ({ value }) => {
                    if (!value) return "never"

                    const lastUsed = new Date(value)
                    return format(lastUsed, DATE_ONLY_FORMAT)
                },
                width: 100,
            }),
            {
                ...buttonColDefCreator({ width: 150 }),
                cellRenderer: ManageSchemaAccessButton("field-forms"),
                headerName: "Actions",
                hide: !canManage,
            },
        ],
        gridSettings: {
            rowHeight: 40,
            defaultColDef: {
                editable: false,
            },
        },
        otherSettings: {
            enableSearchBar: true,
            buttons: {
                table: [
                    ...(is_rhumbix_user && getFlagEnabled("WA-9195-schema-builder-mvp")
                        ? [
                              {
                                  label: "Create Schema",
                                  icon: "add",
                                  action: "openFieldFormSchemaCreate",
                              },
                          ]
                        : []),
                ],
            },
        },
    }

    if (getFlagEnabled("Field-Form-Workflow") || is_rhumbix_user)
        settings.colDefs.push(
            workflowButtonColDefCreator({
                headerName: "Workflow",
                field: "/workflow",
            })
        )

    if (getFlagEnabled("WA-9195-schema-builder-mvp") && is_rhumbix_user) {
        settings.colDefs.push(
            schemaEditorButtonColDefCreator({
                headerName: "Edit/Copy Schema",
                field: "/edit_schema",
            })
        )
    }

    return settings
}

export const getGroupsFieldFormsTabSettings = (featureFlags: tFeatures, currentProject: tProject, currentUser: iCurrentUser): tTableSettings => {
    const base = getFieldFormsDashboardSettings(featureFlags, currentProject, currentUser)
    // Filter out any unnecessary columns
    const groupsDashboardExcludedColumns = ["Groups with Auto-Add", "Actions"]
    base.colDefs = base.colDefs.filter((colDef: tColDef) => colDef.headerName && !groupsDashboardExcludedColumns.includes(colDef.headerName))
    // Make everything read-only
    base.colDefs.forEach((colDef: tColDef) => (colDef.editable = false))

    return base
}
